import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useMyDailyDirectClient,
  BuyerFocusPackageSection,
  OnboardingFocusPackageModal,
} from "@mydailydirect/app-kit";
import {
  Button,
  Card,
  InteractiveLoader,
  useToast,
} from "@mydailydirect/ui";
import {
  Sparkles,
  Store,
  PlusCircle,
  TrendingDown,
  ShoppingBag,
  Package,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRetailerId } from "../lib/useRetailerId";
import { useCart } from "../lib/cart";

export function CuratedForYouPage() {
  const navigate = useNavigate();
  const client = useMyDailyDirectClient();
  const cart = useCart();
  const { toast } = useToast();
  const { retailerId, retailer, buyerName, loading: retailerLoading } = useRetailerId();
  const [isMissingProductModalOpen, setIsMissingProductModalOpen] = useState(false);

  // Fetch skus for package operations
  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
  });

  const skus = skusQuery.data ?? [];

  if (retailerLoading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center">
        <InteractiveLoader title="Loading Your Curated Staples..." />
      </div>
    );
  }

  // Fallback if not authenticated or no retailer profile linked
  if (!retailerId) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 text-center space-y-4">
        <div className="h-16 w-16 mx-auto rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
          <Store className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-black text-foreground">Link Your Store to Access Curated Packs</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Please sign in with your registered phone number or select your shop in Profile to view and order your personalized regular staples.
        </p>
        <Button
          variant="primary"
          onClick={() => navigate("/profile")}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
        >
          Go to Profile
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-4 sm:py-6 px-3 sm:px-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-indigo-600 text-white shadow-xs">
              <Sparkles className="h-3.5 w-3.5" /> Curated For You
            </span>
            <span className="text-xs font-bold text-muted-foreground font-mono">
              {retailer?.name || buyerName || "Your Store"}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
            Your Routine Staples Packet
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl">
            Order your regular inventory requirements with live mandi trends, lowest-price alerts, and 1-click cart replenishment.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMissingProductModalOpen(true)}
            className="text-xs font-bold gap-1.5 border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50/50"
          >
            <PlusCircle className="h-4 w-4" /> Request Missing Item
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/")}
            className="text-xs font-semibold"
          >
            Browse Full Catalog <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>

      {/* Feature Highlights Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border border-indigo-500/20 bg-indigo-50/30 dark:bg-indigo-950/20 p-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-indigo-600/10 text-indigo-600 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">Zero Search Ordering</p>
              <p className="text-2xs text-muted-foreground">Keep focus only on your routine inventory.</p>
            </div>
          </div>
        </Card>

        <Card className="border border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/20 p-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-emerald-600/10 text-emerald-600 flex items-center justify-center shrink-0">
              <TrendingDown className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">Live Rate Trends</p>
              <p className="text-2xs text-muted-foreground">Alerts whenever commodity prices hit 30-day lows.</p>
            </div>
          </div>
        </Card>

        <Card className="border border-amber-500/20 bg-amber-50/30 dark:bg-amber-950/20 p-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-amber-600/10 text-amber-600 flex items-center justify-center shrink-0">
              <ShoppingBag className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">1-Click Replenishment</p>
              <p className="text-2xs text-muted-foreground">Loads standard default quantities into your basket.</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Core BuyerFocusPackageSection */}
      <BuyerFocusPackageSection
        retailerId={retailerId}
        retailerName={retailer?.name || buyerName || "Your Store"}
        client={client}
        availableSkus={skus}
        title="Curated For You — Your Regular Staples"
        onLoadToOrder={(orderItems) => {
          orderItems.forEach((item) => {
            cart.setQty(item.skuId, item.qty, item.packId);
          });
          toast({
            title: "Cart Replenished! 🎯",
            description: `Loaded ${orderItems.length} regular staples into your shopping basket.`,
            variant: "success",
          });
          // Dispatch global cart open event
          window.dispatchEvent(new CustomEvent("mdd:open-cart"));
        }}
      />

      {/* Missing Products Callout Banner */}
      <Card className="border border-border/80 bg-muted/20 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-indigo-600" />
              <h4 className="text-sm font-bold text-foreground">
                Can't find a product your store regularly sells?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground max-w-xl">
              Tell our category sourcing desk what brands, pack sizes, or commodities you need. We source directly from mandi mills and stock them in your curated pack.
            </p>
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsMissingProductModalOpen(true)}
            className="text-xs font-bold border-indigo-400 text-indigo-600 dark:text-indigo-400 shrink-0"
          >
            <PlusCircle className="h-3.5 w-3.5 mr-1" /> Add Product Demand Request
          </Button>
        </div>
      </Card>

      {/* Onboarding / Missing Product Demand Modal */}
      <OnboardingFocusPackageModal
        open={isMissingProductModalOpen}
        onClose={() => setIsMissingProductModalOpen(false)}
        retailerId={retailerId}
        retailerName={retailer?.name || buyerName || "Your Store"}
        availableSkus={skus}
        client={client}
        onComplete={() => {
          setIsMissingProductModalOpen(false);
          toast({
            title: "Demand Request Recorded! 📋",
            description: "Our procurement team has received your item request.",
            variant: "success",
          });
        }}
      />
    </div>
  );
}
